import React from "react";
import { BaseLayout } from "../../shared/components/BaseLayout";
import { InputConnector, InputConnectorsApi } from "../../client";
import { Modal, Space, Table, Tooltip } from "antd";
import { useApiModels } from "../../shared/hooks/useApiModels";
import {
    CommentOutlined,
    EyeOutlined,
    LinkOutlined,
    SlidersOutlined,
    UnorderedListOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { InputConnectorLogModal } from "../components/InputConnectorLogModal";
import { Status } from "../../shared/components/Status";
import { InputConnectorDocumentationModal } from "../components/InputConnectorDocumentationModal";
import { InputConnectorVariablesModal } from "../components/InputConnectorVariablesModal";

interface IProps {}

export const InputConnectorOverview: React.FC<IProps> = (props) => {
    const [inputConnectorId, setInputConnectorId] = React.useState<
        number | null
    >(null);
    const [docsInputConnectorId, setDocsInputConnectorId] = React.useState<
        number | null
    >(null);
    const [variablesInputConnector, setVariablesInputConnector] =
        React.useState<InputConnector | null>(null);

    const {
        models: inputConnectors,
        loading,
        tableProps,
        fetch,
    } = useApiModels(
        (page) => new InputConnectorsApi().listInputConnectors({ page }),
        "generic"
    );

    const setActive = (id: number, newActive: boolean) => {
        Modal.confirm({
            title: "Are you sure you want to make the InputConnector active/inactive?",
            onOk: () =>
                new InputConnectorsApi()
                    .partialUpdateInputConnector({
                        id: String(id),
                        partialInputConnector: {
                            active: newActive,
                        },
                    })
                    .then((res) => {
                        fetch();
                        return res;
                    }),
        });
    };

    return (
        <BaseLayout>
            {inputConnectorId && (
                <InputConnectorLogModal
                    inputConnectorId={inputConnectorId}
                    onClose={() => setInputConnectorId(null)}
                />
            )}
            {docsInputConnectorId && (
                <InputConnectorDocumentationModal
                    inputConnectorId={docsInputConnectorId}
                    onClose={() => setDocsInputConnectorId(null)}
                />
            )}
            {variablesInputConnector && (
                <InputConnectorVariablesModal
                    inputConnector={variablesInputConnector}
                    onClose={(isChanged) => {
                        if (isChanged) {
                            fetch();
                        }
                        setVariablesInputConnector(null);
                    }}
                />
            )}
            <Table<InputConnector>
                dataSource={inputConnectors}
                loading={loading}
                rowKey="id"
                {...tableProps}
                columns={[
                    {
                        key: "id",
                        dataIndex: "id",
                        width: "40px",
                        render: (id, record) => (
                            <Space>
                                <Link to={`/inputs/${id}`}>
                                    <EyeOutlined style={{ color: "black" }} />
                                </Link>
                                <Tooltip title="View documentation">
                                    <CommentOutlined
                                        style={{ color: "black" }}
                                        onClick={() =>
                                            setDocsInputConnectorId(id)
                                        }
                                    />
                                </Tooltip>
                                <Tooltip title="View variables">
                                    <SlidersOutlined
                                        style={{ color: "black" }}
                                        onClick={() =>
                                            setVariablesInputConnector(record)
                                        }
                                    />
                                </Tooltip>
                                {(record.type === "sftp" ||
                                    record.type === "tms") && (
                                    <Tooltip title="View logs">
                                        <UnorderedListOutlined
                                            style={{ color: "black" }}
                                            onClick={() =>
                                                setInputConnectorId(id)
                                            }
                                        />
                                    </Tooltip>
                                )}
                                {record.exact_online_config_id !== null && (
                                    <Tooltip title="Connect to Exact Online">
                                        <LinkOutlined
                                            style={{ color: "black" }}
                                            onClick={() =>
                                                window.open(
                                                    `/api/exact_online_configs/${record.exact_online_config_id}/start_authentication/`
                                                )
                                            }
                                        />
                                    </Tooltip>
                                )}
                            </Space>
                        ),
                    },
                    {
                        key: "name",
                        dataIndex: "name",
                        title: "Name",
                    },
                    {
                        key: "active",
                        dataIndex: "active",
                        title: "Active",
                        render: (isActive, record) => (
                            <div
                                style={{
                                    cursor: "pointer",
                                }}
                                onClick={() => setActive(record.id, !isActive)}
                            >
                                <Status value={isActive} />
                            </div>
                        ),
                    },
                    {
                        key: "type",
                        dataIndex: "type",
                        title: "Type",
                    },
                    {
                        key: "converter_type",
                        dataIndex: "converter_type",
                        title: "Converter type",
                    },
                    {
                        key: "check_every_x_minutes",
                        dataIndex: "check_every_x_minutes",
                        title: "Check every x minutes",
                        render: (minutes) =>
                            minutes > 0 ? `${minutes} min` : "-",
                    },
                    {
                        key: "endpoint",
                        dataIndex: "endpoint",
                        title: "Endpoint",
                    },
                    {
                        key: "is_success",
                        dataIndex: "is_success",
                        title: "Last check successful",
                        render: (success) => <Status value={success} />,
                    },
                ]}
            />
        </BaseLayout>
    );
};
